/**
 * The day in numbers for the timeline's summary (user, 19/10/2026: "I want
 * to see more info"): drops, SMS, cost, how many containers send, how many
 * lists and gateways are used, and the first and last drop. A drop skipped
 * for today is not counted — it does not go out.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanDropRow, DayPlanPreview } from '../../../drop-generator.types';

export interface TimelineFacts {
  drops: number;
  sms: number;
  cost: number | null;
  containers: number;
  lists: number;
  gateways: number;
  firstAt: string | null;
  lastAt: string | null;
}

/** A drop that still goes out today (not skipped or stopped). */
export const liveDrop = (d: DayPlanDropRow): boolean => !d.runtime?.tombstoned;

export const listKey = (d: Pick<DayPlanDropRow, 'listWiserId' | 'listName'>): string | null =>
  d.listWiserId !== null ? `w${d.listWiserId}` : d.listName ? `n${d.listName}` : null;

export function timelineFacts(preview: DayPlanPreview): TimelineFacts {
  const drops = preview.containers.flatMap((c) => c.drops.filter(liveDrop));
  const times = drops.map((d) => d.atUtc).sort();
  const costs = drops.map((d) => d.estCost);
  return {
    drops: drops.length,
    sms: drops.reduce((sum, d) => sum + d.amount, 0),
    cost: costs.length > 0 && costs.every((c) => c !== null) ? costs.reduce((s, c) => s + (c ?? 0), 0) : null,
    containers: preview.containers.filter((c) => c.drops.some(liveDrop)).length,
    lists: new Set(drops.map(listKey).filter(Boolean)).size,
    gateways: new Set(drops.map((d) => d.routeId).filter((r) => r !== null)).size,
    firstAt: times[0] ?? null,
    lastAt: times[times.length - 1] ?? null,
  };
}
